import type { FormatNumberOptions, IntlShape } from "@formatjs/intl";
import { createIntl } from "@formatjs/intl";
import escapeRegExp from "lodash/escapeRegExp";

export interface NumberParser {
  parseFloat(value?: string | number | null | undefined): number;

  parsePercent(value: string): number;

  parseInt(value: string | number | null | undefined): number;

  parseCurrency(value: string): number;

  parseToFixed(value: string, decimalPoints: number): number;
}

export function NumberParser(
  currency: string,
  intl: IntlShape = createIntl({ locale: "en-US" }),
): NumberParser {
  function getSymbolForType(
    intl: IntlShape,
    type: "currency" | "decimal" | "group",
  ): string | undefined {
    function getNumberParts(
      value: number,
      opts?: FormatNumberOptions,
    ): Partial<Record<string, string>> {
      return intl
        .formatNumberToParts(value, opts)
        .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
    }

    switch (type) {
      case "currency":
        return getNumberParts(123, { style: "currency", currency })["currency"];

      default:
        return getNumberParts(12345.67)[type];
    }
  }

  const whiteSpaceRegex = new RegExp("\\s", "g");
  function symbolToRegExp(symbol: string | undefined): RegExp | undefined {
    if (!symbol) {
      return undefined;
    }
    // For symbols that are white space characters, instead use a regex that
    // works for all whitespace characters to handle different non-breaking
    // spaces
    if (symbol.replace(whiteSpaceRegex, "") === "") {
      return whiteSpaceRegex;
    }
    return new RegExp(escapeRegExp(symbol), "g");
  }

  const currencySymbol = getSymbolForType(intl, "currency");
  const decimalSymbol = getSymbolForType(intl, "decimal");
  const groupSymbol = getSymbolForType(intl, "group");

  const groupRegex = symbolToRegExp(groupSymbol);

  const parseIntlFloat = (value?: string | number | null | undefined) => {
    if (value == null) {
      return 0;
    }

    if (typeof value === "number") {
      return value;
    }

    if (groupRegex) {
      value = value.replace(groupRegex, "");
    }
    if (decimalSymbol) {
      value = value.replace(decimalSymbol, ".");
    }

    return parseFloat(value);
  };

  const parseToFixed = (value: string, decimalPoints: number) => {
    const parsed = parseIntlFloat(value);
    return parseFloat(parsed.toFixed(Math.max(0, decimalPoints)));
  };

  return {
    parseFloat: parseIntlFloat,

    parsePercent: (value: string) =>
      parseIntlFloat(value.replace("%", "")) / 100,

    parseInt: (value: string | number | null | undefined) => {
      if (value == null) {
        return 0;
      }

      if (typeof value === "number") {
        return value;
      }

      if (groupRegex) {
        value = value.replace(groupRegex, "");
      }

      return parseInt(value);
    },

    parseCurrency: currencySymbol
      ? (value: string) => parseIntlFloat(value.replace(currencySymbol, ""))
      : parseIntlFloat,

    parseToFixed,
  };
}
